import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
} from '@roger/ui-components';
import {
  Loader2,
  CheckCircle,
  AlertCircle,
  Calendar,
  User,
  Building,
} from 'lucide-react';
import { updateIntake } from '../services/api';
import { useToast } from '../hooks/useToastContext';
import type { AuthorizationStatus, IntakeResponse } from '../services/api';
import { formatDateForDisplay } from '../utils/dateUtils';

interface PriorAuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedIntake: IntakeResponse | null;
  onUpdate: () => Promise<void>;
}

const PriorAuthDialog: React.FC<PriorAuthDialogProps> = ({
  open,
  onOpenChange,
  selectedIntake,
  onUpdate,
}) => {
  const [authNumber, setAuthNumber] = useState('');
  const [authStatus, setAuthStatus] =
    useState<AuthorizationStatus>('NOT_STARTED');
  const [authNotes, setAuthNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { success, error } = useToast();

  // Reset form when intake changes
  React.useEffect(() => {
    if (selectedIntake) {
      setAuthNumber(selectedIntake.authorization_number || '');
      setAuthStatus(selectedIntake.authorization_status || 'NOT_STARTED');
      setAuthNotes(selectedIntake.authorization_notes || '');
    }
  }, [selectedIntake]);

  const handleSubmitPriorAuth = async () => {
    if (!selectedIntake) return;

    setIsSubmitting(true);
    try {
      await updateIntake({
        path: { intake_id: selectedIntake.id },
        body: {
          authorization_number: authNumber.trim() || undefined,
          authorization_status: authStatus,
          authorization_notes: authNotes.trim() || undefined,
          authorization_request_date: new Date().toISOString(),
          authorization_last_updated: new Date().toISOString(),
        },
      });

      success(
        'Prior Authorization Updated',
        'Prior authorization information has been saved.'
      );
      await onUpdate();
      onOpenChange(false);
    } catch (err) {
      console.error('Error updating prior auth:', err);
      error(
        'Update Failed',
        'Failed to update prior authorization information.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    if (!selectedIntake?.insurance_verified) {
      return <AlertCircle className='h-5 w-5 text-gray-600' />;
    }

    switch (status) {
      case 'APPROVED':
        return <CheckCircle className='h-5 w-5 text-green-500' />;
      case 'DENIED':
        return <AlertCircle className='h-5 w-5 text-red-500' />;
      default:
        return <Calendar className='h-5 w-5 text-yellow-500' />;
    }
  };

  const getStatusColor = (status: string) => {
    if (!selectedIntake?.insurance_verified) {
      return 'bg-gray-100 text-gray-600';
    }

    switch (status) {
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'DENIED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const formatDate = (date: string | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getPatientName = (intake: IntakeResponse) => {
    const firstName = intake.patient_first_name || '';
    const lastName = intake.patient_last_name || '';
    return firstName || lastName ? `${firstName} ${lastName}`.trim() : 'N/A';
  };

  const isFormValid =
    authStatus !== 'NOT_STARTED' || authNumber.trim() || authNotes.trim();

  if (!selectedIntake) return null;

  const intakeAuthStatus = selectedIntake.authorization_status || 'NOT_STARTED';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>
            Prior Authorization - {getPatientName(selectedIntake)}
          </DialogTitle>
        </DialogHeader>

        <div className='space-y-6'>
          {/* Patient Info Summary */}
          <div className='p-4 bg-gray-50 rounded-lg space-y-2'>
            <div className='flex items-center gap-2'>
              <User className='h-4 w-4 text-muted-foreground' />
              <span className='font-medium'>
                {getPatientName(selectedIntake)}
              </span>
              {selectedIntake.patient_dob && (
                <span className='text-muted-foreground'>
                  • DOB:{' '}
                  {formatDateForDisplay(selectedIntake.patient_dob ?? '') ||
                    'N/A'}
                </span>
              )}
            </div>
            <div className='flex items-center gap-2'>
              <Building className='h-4 w-4 text-muted-foreground' />
              <span>{selectedIntake.primary_insurance || 'N/A'}</span>
              {selectedIntake.primary_member_id && (
                <span className='text-muted-foreground'>
                  • ID: {selectedIntake.primary_member_id}
                </span>
              )}
            </div>
          </div>

          {/* Current Status */}
          <div className='p-4 bg-gray-50 rounded-lg space-y-3'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-3'>
                {getStatusIcon(intakeAuthStatus)}
                <div>
                  <div className='font-medium'>Current Status</div>
                  <Badge className={getStatusColor(intakeAuthStatus)}>
                    {!selectedIntake.insurance_verified
                      ? 'Cannot be determined'
                      : intakeAuthStatus === 'APPROVED'
                        ? 'Approved'
                        : intakeAuthStatus === 'DENIED'
                          ? 'Denied'
                          : intakeAuthStatus === 'NOT_STARTED'
                            ? 'Not Started'
                            : 'Pending'}
                  </Badge>
                </div>
              </div>
              {selectedIntake.authorization_last_updated && (
                <div className='text-sm text-muted-foreground'>
                  Last updated:{' '}
                  {formatDate(selectedIntake.authorization_last_updated)}
                </div>
              )}
            </div>

            {/* Authorization Number */}
            {selectedIntake.authorization_number && (
              <div>
                <span className='text-sm font-medium text-gray-700'>
                  Authorization Number:{' '}
                </span>
                <span className='text-sm text-gray-900'>
                  {selectedIntake.authorization_number}
                </span>
              </div>
            )}

            {/* Comments */}
            {selectedIntake.authorization_notes && (
              <div>
                <span className='text-sm font-medium text-gray-700'>
                  Notes:{' '}
                </span>
                <div className='text-sm text-gray-900 mt-1 p-2 bg-white rounded border'>
                  {selectedIntake.authorization_notes}
                </div>
              </div>
            )}
          </div>

          {/* Manual Form */}
          <div className='space-y-4'>
            <h4 className='font-medium'>Update Authorization</h4>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Authorization Number
                </label>
                <Input
                  value={authNumber}
                  onChange={e => setAuthNumber(e.target.value)}
                  placeholder='Enter authorization number...'
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Authorization Status
                </label>
                <Select
                  value={authStatus}
                  onValueChange={v => setAuthStatus(v as AuthorizationStatus)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='NOT_STARTED'>Not Started</SelectItem>
                    <SelectItem value='PENDING'>Pending</SelectItem>
                    <SelectItem value='APPROVED'>Approved</SelectItem>
                    <SelectItem value='DENIED'>Denied</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Notes
              </label>
              <Textarea
                value={authNotes}
                onChange={e => setAuthNotes(e.target.value)}
                placeholder='Enter comments about the authorization...'
                rows={4}
              />
            </div>
          </div>

          {/* Actions */}
          <div className='flex justify-end space-x-2'>
            <Button
              variant='outline'
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitPriorAuth}
              disabled={isSubmitting || !isFormValid}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className='h-4 w-4 animate-spin' />
                  Updating...
                </>
              ) : (
                'Update Prior Authorization'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PriorAuthDialog;
